import type { EntityConfigDefinition, DisplayColumnConfig, FilterConfig } from '../../types/entityConfig'
import { TABLE_SERVICE } from '../../utils/displayFormatters'

const config: EntityConfigDefinition = {
  name: 'SERVICE_PARAM',
  idField: 'SERVICE_PARAM_ID',
  
  // Form processing configuration
  keepAuditFieldsOnUpdate: false,
  
  // Environment copy configuration
  preserveOnCopy: ['SERVICE_PARAM_ID'],
  
  // SERVICE_PARAM shows list filtered by service, not optimized for single param details
  useOptimizedQuery: false,
  
  // Foreign key configuration for table display
  foreignKeys: {
    SERVICE_ID: {
      table: 'SERVICE',
      valueField: 'SERVICE_ID',
      displayField: 'URI'
    },
    DATA_ELEMENT_ID: {
      table: 'DATA_ELEMENT',
      valueField: 'DATA_ELEMENT_ID',
      displayField: 'ELEMENT_KEY'
    }
  },
  
  // Fields to remove before GraphQL mutations
  fieldsToRemove: ['SERVICE_ID_DISPLAY'],
  
  // Field lookups for display enhancement
  fieldLookups: {
    SERVICE_ID: {
      lookupTable: 'SERVICE',
      foreignKey: 'SERVICE_ID',
      displayField: 'URI',
      displayFormat: TABLE_SERVICE
    },
    DATA_ELEMENT_ID: {
      lookupTable: 'DATA_ELEMENT',
      foreignKey: 'DATA_ELEMENT_ID',
      displayField: 'ELEMENT_KEY',
      displayFormat: '{DATA_ELEMENT_ID}: {ELEMENT_KEY}'
    }
  },
  
  // Auto-create missing foreign key records
  autoCreateForeignKeys: {
    SERVICE_ID: {
      entity: 'SERVICE',
      matchFields: ['URI'],
      copyFields: ['SERVICE_PROVIDER_ID', 'URI', 'SECRET_NAME', 'REQUEST_TYPE']
    }
  },
  
  // Environment comparison configuration
  comparisonConfig: {
    matchingFields: ['PARAM_NAME'],
    stringMatchFields: ['PARAM_NAME'],
    stringMatchThreshold: 0.50,
    comparisonFields: ['SERVICE_ID', 'PARAM_NAME', 'IS_SENSITIVE', 'DATA_ELEMENT_ID'],
    useDisplayValues: {
      'SERVICE_ID': 'URI',
      'DATA_ELEMENT_ID': 'ELEMENT_KEY'
    },
    displayFieldMapping: {
      'SERVICE_ID': 'URI',
      'DATA_ELEMENT_ID': 'ELEMENT_KEY'
    },
    requiresServiceFilter: true,
    showEmptyUntilFiltered: true,
    serviceFilterField: 'SERVICE_ID',
    allowBulkOperations: true,
    crossEnvironmentMapping: {
      SERVICE_ID: {
        matchFields: ['Service Provider', 'URI']
      }
    },
    displayFields: ['SERVICE_PARAM_ID', 'SERVICE_ID', 'PARAM_NAME', 'IS_SENSITIVE', 'DATA_ELEMENT_ID', 'CREATED_BY_USER_ID', 'CREATED_DATE', 'CHANGED_BY_USER_ID', 'CHANGED_DATE'],
    resolveLookupsForBothEnvironments: true
  },
  
  // Relationship counting configuration
  relationships: {
    mappings: {
      query: 'listServiceParamMapping',
      dataKey: 'listSERVICE_PARAM_MAPPING',
      countFields: ['SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID']
    }
  },
  
  // Auto-fill configuration
  autoFillFromFilter: {
    filterField: 'selectedServiceFilter',
    formField: 'SERVICE_ID'
  },
  
  // Data loading configuration
  loadServiceOptions: true,
  
  // Filter configuration
  hasFilters: true,
  filterType: 'service',
  requiresServiceFilter: true,
  showEmptyUntilFiltered: true,
  
  // Custom filters for this entity
  customFilters: {
    service: {
      label: 'Filter by Service',
      field: 'SERVICE_ID',
      type: 'service',
      required: true,
      showAlways: true
    }
  },
  
  // Display columns with foreign key lookups
  displayColumns: [
    {
      field: 'SERVICE_ID',
      lookup: {
        table: 'SERVICE',
        foreignKey: 'SERVICE_ID',
        displayField: 'URI'
      }
    },
    {
      field: 'DATA_ELEMENT_ID',
      lookup: {
        table: 'DATA_ELEMENT',
        foreignKey: 'DATA_ELEMENT_ID',
        displayField: 'ELEMENT_KEY'
      }
    }
  ] as DisplayColumnConfig[],

  // Unified filter configuration
  filters: {
    table: [
      { type: 'service', field: 'SERVICE_ID', label: 'Filter by Service', required: true }
    ] as FilterConfig[],
    requiresService: true,
    showEmptyUntilFiltered: true
  },

  // Fields configuration
  fields: [
    "SERVICE_PARAM_ID",
    "SERVICE_ID",
    "PARAM_NAME",
    "IS_SENSITIVE",
    "DATA_ELEMENT_ID",
    "CREATED_BY_USER_ID",
    "CREATED_DATE",
    "CHANGED_BY_USER_ID",
    "CHANGED_DATE"
  ],
  
  // Form fields configuration
  formFields: [
    {
      name: "SERVICE_ID",
      type: "select",
      required: true,
      disabled: false
    },
    {
      name: "PARAM_NAME",
      type: "text",
      required: true,
      disabled: false
    },
    {
      name: "IS_SENSITIVE",
      type: "checkbox",
      required: false,
      disabled: false
    },
    {
      name: "DATA_ELEMENT_ID",
      type: "select",
      required: false,
      disabled: false
    },
    {
      name: "CREATED_BY_USER_ID",
      type: "number",
      required: false,
      disabled: false
    },
    {
      name: "CREATED_DATE",
      type: "text",
      required: false,
      disabled: true
    },
    {
      name: "CHANGED_BY_USER_ID",
      type: "number",
      required: false,
      disabled: false
    },
    {
      name: "CHANGED_DATE",
      type: "text",
      required: false,
      disabled: true
    }
  ]
}

export default config
